import React, { ChangeEvent, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles, TextField, Tooltip } from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState, getProjectUsersState } from '../../../features/project/selectors';
import { getCompaniesState } from '../../../features/companies/selector';
import { ICompany, IUserGroup } from '../../../api-client/autogenerated';
import { CancelButton } from '../../custom-components/CustomButtons';
import Button from '@material-ui/core/Button';
import { editUserGroup, insertUserGroup } from '../../../models/api/user-groups';
import CircularLoader from '../../loader/CircularLoader';
import { addSnackbar } from '../../../features/snackbar/actions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import DistributionList from './DistributionList';
import _ from 'lodash';
import { ManagePermissionsDialogType } from '../../design/ManagePermissionsDialog';
import CompanyUserTreeView from './CompanyUserTreeView';
import { getGroupsState } from '../../../features/groups/selector';

const useStyles = makeStyles((theme) => ({
  mobileButton: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
      width: '90%',
      padding: 0,
    },
  },
}));

function getArrayDifference<T>(allElements: T[], existingElements: T[]) {
  return allElements.filter(
    (userId) => !existingElements.some((existingUserId) => userId === existingUserId),
  );
}

type Props = {
  existingGroup?: IUserGroup | null;
  onSubmit?: (userGroup: IUserGroup) => void;
  type?: ManagePermissionsDialogType;
};

export default function CreateUserGroup(props: Props) {
  const { existingGroup, onSubmit, type } = props;
  const classes = useStyles();

  const groups = useSelector(getGroupsState);
  const project = useSelector(getProjectState);
  const projectUsers = useSelector(getProjectUsersState);
  const companies = useSelector(getCompaniesState);
  const dispatch = useDispatch();

  const isDesign = type === ManagePermissionsDialogType.Design;

  const [eligibleCompanies, setEligibleCompanies] = useState<ICompany[]>([]);
  useEffect(() => {
    setEligibleCompanies(
      companies
        .map((company) => {
          return {
            ...company,
            users: company.users?.filter(({ id }) =>
              projectUsers.some(({ userId }) => userId === id),
            ),
          };
        })
        .filter(({ users }) => users && users.length > 0),
    );
  }, [companies]);

  const [inputName, setInputName] = useState<string>();
  const [inputDescription, setInputDescription] = useState<string | null>();

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(!isDesign);

  // EDITING ONLY
  const [existingUsers, setExistingUsers] = useState<string[]>([]);

  const getFieldsFromGroup = (existingGroup: IUserGroup) => {
    setInputName(existingGroup.name);
    setInputDescription(existingGroup.description);
    const users = existingGroup.userGroupUserList?.slice().map(({ userId }) => userId) || [];
    setSelectedUsers(users);
    setExistingUsers(users);
  };

  useEffect(() => {
    resetFields();
    if (existingGroup) getFieldsFromGroup(existingGroup);
    else resetFieldsExisting();
  }, [existingGroup]);

  const getPendingUsers = () => {
    const pendingUsers = eligibleCompanies
      .flatMap((company) => company.users || [])
      .filter((user) => selectedUsers.includes(user.id));
    return _.uniqBy(pendingUsers, (user) => user.id);
  };

  const handleCheckCompany = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    companyId: string,
  ) => {
    const companyUserIds =
      eligibleCompanies.find(({ id }) => companyId === id)?.users?.map(({ id }) => id) || [];
    if (checked) setSelectedUsers((prev) => _.uniq([...prev, ...companyUserIds]));
    else setSelectedUsers((prev) => prev.filter((userId) => !companyUserIds.includes(userId)));
  };

  const handleCheckNestedUser = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    userId: string,
    _companyId: string,
  ) => {
    if (checked) setSelectedUsers((prev) => _.uniq([...prev, userId]));
    else setSelectedUsers((prev) => prev.filter((id) => id !== userId));
  };

  const resetFields = () => {
    setInputName('');
    setInputDescription('');
    setSelectedUsers([]);
  };

  const resetFieldsExisting = () => {
    setExistingUsers([]);
  };

  const handleReset = () => {
    if (existingGroup) getFieldsFromGroup(existingGroup);
    else resetFields();
  };

  const handleCreate = async () => {
    if (!inputName || !project) return;
    setIsSubmitting(true);
    try {
      let newGroup: IUserGroup;
      if (existingGroup) {
        const usersToAdd = getArrayDifference(selectedUsers, existingUsers)?.map((userId) => {
          return { userId };
        });
        const usersToRemove = getArrayDifference(existingUsers, selectedUsers);
        const userGroupIdsToRemove = usersToRemove
          ?.map(
            (userId) =>
              existingGroup.userGroupUserList?.find((group) => group.userId === userId)?.id,
          )
          .filter((id) => id) as string[];

        newGroup = await editUserGroup(
          existingGroup.id,
          inputName !== existingGroup.name ? inputName : undefined,
          inputDescription !== existingGroup.description ? inputDescription || '' : undefined,
          usersToAdd,
          userGroupIdsToRemove,
        );
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: `${existingGroup.name} modified successfully!`,
            severity: 'success',
          }),
        );
      } else {
        newGroup = await insertUserGroup(
          project.id,
          inputName.trim(),
          inputDescription?.trim(),
          selectedUsers.map((userId) => {
            return { userId };
          }),
        );
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: `${newGroup.name} created successfully!`,
            severity: 'success',
          }),
        );
        resetFields();
      }
      if (onSubmit) onSubmit(newGroup);
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const newGroupHasSameName =
    !existingGroup &&
    groups.some(({ name }) => name?.trim().toLowerCase() === inputName?.trim().toLowerCase());

  const getSubmitDisabled = () => {
    if (existingGroup)
      return (
        inputName === existingGroup.name &&
        inputDescription === existingGroup.description &&
        _.isEqual(selectedUsers, existingUsers)
      );
    else return !inputName?.trim() || selectedUsers.length === 0 || newGroupHasSameName;
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={5}>
        <TextField
          size="small"
          variant="outlined"
          fullWidth
          label={existingGroup ? undefined : 'Name'}
          required
          value={inputName}
          error={newGroupHasSameName}
          helperText={
            newGroupHasSameName ? (
              'A group with this name already exists.'
            ) : (
              <div style={{ height: 20 }} />
            )
          }
          onChange={(e) => setInputName(e.target.value)}
        />
        {type !== ManagePermissionsDialogType.None && (
          <Typography style={{ marginTop: '16px' }}>
            Users listed with a <del>strikethrough</del> do not have permission to view{' '}
            {type === ManagePermissionsDialogType.AssociatedUsers
              ? 'this document.'
              : type === ManagePermissionsDialogType.ProjectLifecycle
              ? 'these forms.'
              : 'the Design tab.'}
          </Typography>
        )}
        <Tooltip
          title={'This list only shows users who have completed Centerline user registration.'}
          arrow
          placement={'right'}
        >
          <Typography style={{ width: 'fit-content', marginTop: '16px' }}>
            <i>Missing users?</i>
          </Typography>
        </Tooltip>
      </Grid>
      <Grid item xs={7}>
        <TextField
          size="small"
          multiline
          rows={5}
          variant="outlined"
          fullWidth
          label="Description (optional)"
          value={inputDescription}
          onChange={(e) => setInputDescription(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <CompanyUserTreeView
          type={type}
          companies={eligibleCompanies}
          selectedUsers={selectedUsers}
          handleCheckCompany={handleCheckCompany}
          handleCheckNestedUser={handleCheckNestedUser}
        />
      </Grid>
      <Grid item xs={12} style={{ marginTop: 16 }}>
        <div style={{ display: 'flex' }}>
          <IconButton onClick={() => setOpen((prev) => !prev)} style={{ padding: 0 }}>
            {open ? <Remove /> : <Add />}
          </IconButton>
          <Typography
            variant="h1"
            style={{ fontSize: 22, lineHeight: '22px', textTransform: 'none' }}
          >
            {type === ManagePermissionsDialogType.Design
              ? 'Pending Distribution List:'
              : 'Pending Notification List:'}
          </Typography>
        </div>
        <DistributionList open={open} users={getPendingUsers()} />
      </Grid>
      <Grid
        item
        container
        justify="space-between"
        wrap="nowrap"
        style={{ padding: '16px 24px 16px 24px' }}
      >
        <CancelButton onClick={handleReset}>Reset</CancelButton>
        {isSubmitting ? (
          <CircularLoader />
        ) : (
          <Button
            variant="contained"
            color="primary"
            disabled={getSubmitDisabled()}
            onClick={handleCreate}
            className={classes.mobileButton}
          >
            {existingGroup ? 'Modify Group' : 'Create Group'}
          </Button>
        )}
      </Grid>
    </Grid>
  );
}
